import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { bootstrapApplication } from '@angular/platform-browser';
import { CandidateTableComponent, CellAction, EditContractRequest } from './candidate-table.component';
import { ActionModalComponent } from './action-modal.component';
import { MissionInfoComponent } from './mission-info.component';
import { Candidate, Mission, ModalData } from '../interfaces/candidate.interface';
import { DateService } from '../services/date.service';
import { MissionFragmentCandidatureService } from '../../../services/mission-fragment-candidature.service';
import { catchError, finalize } from 'rxjs/operators';
import { of, EMPTY, interval, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ContractPreviewDialogComponent } from '../../../shared/contract-preview-dialog/contract-preview-dialog.component';

@Component({
  selector: 'app-fragmented-mission-actions',
  standalone: true,
  imports: [
    CommonModule, 
    CandidateTableComponent, 
    ActionModalComponent, 
    MissionInfoComponent
  ],
  // Dans le template
  template: `
    <div class="px-px-24  py-px-1" style="background: #F3F8FA;">
      <h1 class="mt-4">Gestion des missions</h1>
  
      <main>
        <app-mission-info 
          [mission]="mission"
          (missionChange)="onMissionChange($event)">
        </app-mission-info>
        
        <app-candidate-table
          [candidates]="candidates"
          [mission]="mission"
          (acceptCandidate)="onAcceptCandidate($event)"
          (rejectCandidate)="onRejectCandidate($event)"
          (cancelAccepted)="onCancelAccepted($event)"
          (cancelSigned)="onCancelSigned($event)"
          (cellActionRequested)="onCellActionRequested($event)"
          (editContractRequested)="onEditContractRequested($event)">
        </app-candidate-table>
        
        <app-action-modal
          *ngIf="modalData && showModal"
          [modalData]="modalData"
          [isVisible]="showModal"
          (close)="onModalClose()"
          (confirm)="onModalConfirm($event)">
        </app-action-modal>
        
        <!-- Loader overlay -->
        <div *ngIf="showLoader" class="loader-overlay">
          <div class="loader-container">
            <div class="spinner"></div>
            <p class="loader-message">{{ loaderMessage }}</p>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    .app-container {
      min-height: 100vh;
      background-color: #f5f5f5;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .app-header {
      background-color: #343a40;
      color: white;
      padding: 16px 24px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .app-header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
    }

    .app-main {
      padding: 24px;
      max-width: 1400px;
      margin: 0 auto;
    }

    @media (max-width: 768px) {
      .app-main {
        padding: 16px;
      }
    }
    
    /* Loader styles */
    .loader-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    }
    
    .loader-container {
      background-color: white;
      padding: 30px;
      border-radius: 8px;
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #3498db;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .loader-message {
      font-size: 16px;
      margin: 0;
    }
  `]
})
export class fragmentActionsComponent implements OnInit {
  mission: Mission;
  candidates: Candidate[] = [];
  modalData: ModalData | null = null;
  showModal: boolean = false;
  
  // Loader properties
  showLoader: boolean = false;
  loaderMessage: string = '';
  private messageIndex: number = 0;
  private messageInterval: Subscription | null = null;
  private loaderMessages: string[] = [
    'Traitement de votre action en cours',
    'En train de mettre à jour les statuts',
    'Envoi des notifications en cours'
  ];

  constructor(
    private fragmentService: MissionFragmentCandidatureService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    // Get mission ID from route parameters
    this.route.params.subscribe(params => {
      const missionId = +params['id']; // Convert to number
      if (missionId) {
        this.loadCandidatures(missionId);
      }
    });
  }

  // Start the loader with rotating messages
  private startLoader(): void {
    this.showLoader = true;
    this.messageIndex = 0;
    this.loaderMessage = this.loaderMessages[this.messageIndex];
    
    // Change message every 3 seconds
    this.messageInterval = interval(3000).subscribe(() => {
      this.messageIndex = (this.messageIndex + 1) % this.loaderMessages.length;
      this.loaderMessage = this.loaderMessages[this.messageIndex];
    });
  }

  // Stop the loader
  private stopLoader(): void {
    if (this.messageInterval) {
      this.messageInterval.unsubscribe();
      this.messageInterval = null;
    }
    this.showLoader = false;
  }

  onMissionChange(mission: Mission): void {
    this.mission = { ...mission };
  }

  onAcceptCandidate(candidate: Candidate): void {
    this.modalData = {
      candidate: candidate,
      dates: DateService.generateDateRange(this.mission.startDate, this.mission.endDate),
      action: 'accept'
    };
    this.showModal = true;
  }

  onRejectCandidate(candidate: Candidate): void {
    this.modalData = {
      candidate: candidate,
      dates: DateService.generateDateRange(this.mission.startDate, this.mission.endDate),
      action: 'reject'
    };
    this.showModal = true;
  }

  onCancelAccepted(candidate: Candidate): void {
    this.modalData = {
      candidate: candidate,
      dates: DateService.generateDateRange(this.mission.startDate, this.mission.endDate),
      action: 'cancel-accepted'
    };
    this.showModal = true;
  }

  onCancelSigned(candidate: Candidate): void {
    this.modalData = {
      candidate: candidate,
      dates: DateService.generateDateRange(this.mission.startDate, this.mission.endDate),
      action: 'cancel-signed'
    };
    this.showModal = true;
  }

  onModalClose(): void {
    this.modalData = null;
    this.showModal = false;
  }

  onModalConfirm(selectedDatesOrResult: string[]): void {
    if (!this.modalData) return;

    const candidate = this.modalData.candidate;
    const action = this.modalData.action;
    
    // Traiter l'action spéciale 'proposed-options'
    if (action === 'proposed-options') {
      // Vérifier si le résultat est une chaîne JSON
      if (selectedDatesOrResult.length === 1 && typeof selectedDatesOrResult[0] === 'string' && 
          selectedDatesOrResult[0].startsWith('{')) {
        try {
          const result = JSON.parse(selectedDatesOrResult[0]);
          if (result.action === 'accept') {
            // Créer un nouveau modalData pour l'action d'acceptation
            this.modalData = {
              candidate: candidate,
              dates: result.dates,
              action: 'accept'
            };
            // Appeler directement onModalConfirm avec les dates
            this.onModalConfirm(result.dates);
          } else if (result.action === 'reject') {
            // Créer un nouveau modalData pour l'action de rejet
            this.modalData = {
              candidate: candidate,
              dates: result.dates,
              action: 'reject'
            };
            // Appeler directement onModalConfirm avec les dates
            this.onModalConfirm(result.dates);
          }
          return;
        } catch (e) {
          console.error('Error parsing result:', e);
        }
      }
      return;
    }
    
    // Le reste du code reste inchangé
    // Get the fragment IDs for the selected dates
    const fragmentIds = this.getFragmentIdsForDates(candidate, selectedDatesOrResult);
    
    if (fragmentIds.length === 0) {
      console.error('No fragment IDs found for selected dates');
      this.onModalClose();
      return;
    }

    // Start the loader before API calls
    this.startLoader();
    this.onModalClose(); // Close the modal but keep the loader

    // Call the appropriate API based on the action
    switch (action) {
      case 'accept':
        this.fragmentService.acceptFragment(fragmentIds)
          .pipe(
            catchError(error => {
              console.error('Error accepting fragments:', error);
              this.stopLoader(); // Stop loader on error
              return of(null);
            })
          )
          .subscribe(response => {
            if (response) {
              this.updateCandidateApplications(candidate, selectedDatesOrResult, 'CONTRACT_PENDING_VALIDATION');
              this.notifyUser(candidate, selectedDatesOrResult, 'accept');
            }
            this.stopLoader(); // Stop loader after completion
          });
        break;
        
      case 'reject':
        this.fragmentService.refuseFragment(fragmentIds)
          .pipe(
            catchError(error => {
              console.error('Error rejecting fragments:', error);
              this.stopLoader(); // Stop loader on error
              return of(null);
            })
          )
          .subscribe(response => {
            if (response) {
              this.updateCandidateApplications(candidate, selectedDatesOrResult, 'REJECTED');
              this.notifyUser(candidate, selectedDatesOrResult, 'reject');
            }
            this.stopLoader(); // Stop loader after completion
          });
        break;
        
      case 'cancel-accepted':
        this.fragmentService.cancelAcceptedFragment(fragmentIds)
          .pipe(
            catchError(error => {
              console.error('Error canceling accepted fragments:', error);
              this.stopLoader(); // Stop loader on error
              // EMPTY (not of(null)): a refused call (e.g. 403) must not run the success branch below.
              return EMPTY;
            })
          )
          .subscribe(() => {
            this.updateCandidateApplications(candidate, selectedDatesOrResult, 'PROPOSED');
            this.notifyUser(candidate, selectedDatesOrResult, 'cancel-accepted');
            this.stopLoader(); // Stop loader after completion
          });
        break;
        
      case 'cancel-signed':
        this.fragmentService.cancelSignedFragment(fragmentIds)
          .pipe(
            catchError(error => {
              console.error('Error canceling signed fragments:', error);
              this.stopLoader(); // Stop loader on error
              // EMPTY (not of(null)): a refused call (e.g. 403) must not run the success branch below.
              return EMPTY;
            })
          )
          .subscribe(() => {
            this.updateCandidateApplications(candidate, selectedDatesOrResult, 'PROPOSED');
            this.notifyUser(candidate, selectedDatesOrResult, 'cancel-signed');
            this.stopLoader(); // Stop loader after completion
          });
        break;
    }
  }

  /**
   * Get fragment IDs for the selected dates
   * @param candidate The candidate
   * @param selectedDates The selected dates
   * @returns Array of fragment IDs
   */
  private getFragmentIdsForDates(candidate: Candidate, selectedDates: string[]): number[] {
    const fragmentIds: number[] = [];
    
    candidate.applications.forEach(app => {
      const appDates = DateService.generateDateRange(app.fromDate, app.toDate);
      const hasSelectedDate = appDates.some(date => selectedDates.includes(date));
      
      if (hasSelectedDate) {
        fragmentIds.push(app.id);
      }
    });
    
    return fragmentIds;
  }

  /**
   * Update candidate applications with new status
   * @param candidate The candidate
   * @param selectedDates The selected dates
   * @param newStatus The new status
   */
  private updateCandidateApplications(candidate: Candidate, selectedDates: string[], newStatus: string): void {
    candidate.applications.forEach(app => {
      const appDates = DateService.generateDateRange(app.fromDate, app.toDate);
      const hasSelectedDate = appDates.some(date => selectedDates.includes(date));
      
      if (hasSelectedDate) {
        app.status = newStatus as any;
      }
    });
  }

  /**
   * Notify the user about the action
   * @param candidate The candidate
   * @param selectedDates The selected dates
   * @param action The action performed
   */
  private notifyUser(candidate: Candidate, selectedDates: string[], action: string): void {
    const actionMessages = {
      'accept': 'acceptées',
      'reject': 'refusées',
      'cancel-accepted': 'annulées (non signées)',
      'cancel-signed': 'annulées (signées)'
    };

    // console.log(`
    //   📧 Email envoyé à l'agence et au candidat ${candidate.firstName} ${candidate.name}
    //   Action: Dates ${actionMessages[action as keyof typeof actionMessages]}
    //   Dates concernées: ${selectedDates.map(d => DateService.formatDate(d)).join(', ')}
    // `);

    // In a real application, this would trigger actual email sending
    // alert(`Email de notification envoyé pour les dates ${actionMessages[action as keyof typeof actionMessages]} du candidat ${candidate.firstName} ${candidate.name}`);
    // Reload only the data instead of the entire page to keep dialogs accessible
    if (this.mission) {
      this.loadCandidatures(this.mission.id);
    }
  }

  /**
   * Load candidatures from the API
   * @param missionId The ID of the mission to load candidatures for
   */
  private loadCandidatures(missionId: number): void {
    this.fragmentService.getFragmentsByMission(missionId)
      .pipe(
        catchError(error => {
          console.error('Error loading candidatures:', error);
          return of(null);
        })
      )
      .subscribe(data => {
        if (data) {
          console.log('Received data:', data);
          
          // Update mission details if available
          if (data.mission) {
            this.mission = {
              id: data.mission.id,
              reference: data.mission.reference,
              post: data.mission.post,
              startDate: data.mission.startDate,
              endDate: data.mission.endDate,
              establishment: data.mission.establishment,
              establishmentId: data.mission.establishmentId,
              site: data.mission.site,
              assignmentService: data.mission.assignmentService,
              dailyHours: data.mission.dailyHours || [],
              startTime: data.mission.startTime || null,
              pauseTime: data.mission.pauseTime ?? 0,
              endTime: data.mission.endTime || null,
              typeSecteurId: data.mission.typeSecteurId,
              typeSecteur: data.mission.typeSecteur
            };
          }
          
          // Update candidates list
          if (data.candidates && Array.isArray(data.candidates)) {
            this.candidates = data.candidates.map((candidate: any) => ({
              id: candidate.id,
              name: candidate.name,
              firstName: candidate.firstName,
              note: candidate.note,
              applications: candidate.applications.map((app: any) => ({
                id: app.id,
                fromDate: app.fromDate,
                toDate: app.toDate,
                status: app.status,
                candidateId: app.candidateId,
                candidateName: app.candidateName
              }))
            }));
          }
        }
      });
  }

    // Ajouter cette méthode dans la classe
    onCellActionRequested(cellAction: CellAction): void {
      if (cellAction.status === 'PROPOSED') {
        // Pour les cellules orange (PROPOSED), ouvrir un modal avec options
        this.modalData = {
          candidate: cellAction.candidate,
          dates: [cellAction.date],
          action: 'proposed-options'
        };
        this.showModal = true;
      }
    }

    onEditContractRequested(req: EditContractRequest): void {      
      if (!this.mission) return;
      const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
        width: '95vw',
        maxWidth: '1400px',
        height: '92vh',
        panelClass: 'contract-preview-panel',
        autoFocus: false,
        data: {
          missionId: this.mission.id,
          candidatId: req.candidate.id,
          applicationId: req.applicationId,
          mission: this.mission
        }
      });

      dialogRef.afterClosed().subscribe((res: any) => {
        if (res?.sent || res?.missionSent || res?.dispositionSent) {
          this.loadCandidatures(this.mission.id);
        }
      });
    }
}
  
